import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sun, 
  Moon, 
  Sunrise,
  Sunset,
  Sparkles,
  ArrowRight,
  X,
  Heart,
  User,
  Target,
  Compass,
  BookOpen
} from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

const WELCOME_LAST_SHOWN_KEY = 'dmu-rimp-welcome-last-shown';
const WELCOME_DISABLED_KEY = 'dmu-rimp-welcome-disabled';

// Helper to get all days
const getAllDays = () => {
  const days = [];
  retreatContent.weeks.forEach(week => {
    week.days.forEach(day => {
      days.push({ ...day, weekNumber: week.number, pillar: week.pillar });
    });
  });
  return days;
};

// Daily challenges
const dailyChallenges = [
  "Spend 5 extra minutes in silence today",
  "Write a gratitude list of 3 things",
  "Pray for someone you find difficult",
  "Fast from social media until noon",
  "Call or text someone you haven't spoken to",
  "Do one act of kindness anonymously",
  "Pray the Rosary or Divine Mercy Chaplet",
  "Read an extra Scripture passage",
  "Write a letter to God",
  "Spend time in nature, noticing God's creation",
  "Practice the Examen twice today",
  "Offer your work as a prayer",
  "Smile at every person you meet",
  "Give away something you no longer need",
  "Listen more than you speak today",
  "Pray for the Holy Father's intentions",
  "Fast from complaining for 24 hours",
  "Write down 3 ways God blessed you this week",
  "Reach out to a friend going through difficulty",
  "Practice silence during a meal",
  "Pray the Stations of the Cross",
  "Do something special for a family member",
  "Spend 10 minutes just listening to God",
  "Forgive someone who hurt you",
  "Make a small sacrifice and offer it up",
  "Write about how you've grown this month",
  "Share your faith journey with someone",
  "Thank God for your struggles",
  "Prepare your heart for the final day",
  "Celebrate! Share your experience with others"
];

const pillarInfo = {
  Relationship: { icon: Heart, color: 'rose' },
  Identity: { icon: User, color: 'amber' },
  Mission: { icon: Target, color: 'teal' },
  Purpose: { icon: Compass, color: 'violet' }
};

const WelcomeBackModal = ({ currentDay, dailyNotes, onClose }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const allDays = getAllDays();
  const todayInfo = allDays.find(d => d.day === currentDay);
  const yesterdayNote = dailyNotes[currentDay - 1];

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: 'Good Morning', icon: Sunrise };
    if (hour < 17) return { text: 'Good Afternoon', icon: Sun };
    if (hour < 21) return { text: 'Good Evening', icon: Sunset };
    return { text: 'Good Night', icon: Moon };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;
  const pillar = todayInfo?.pillar || 'Relationship';
  const PillarIcon = pillarInfo[pillar]?.icon || Heart;
  const pillarColor = pillarInfo[pillar]?.color || 'rose';
  const todayChallenge = currentDay > 0 && currentDay <= 30 ? dailyChallenges[currentDay - 1] : null;

  useEffect(() => {
    // Check if we should show the welcome modal
    const isDisabled = localStorage.getItem(WELCOME_DISABLED_KEY) === 'true';
    if (isDisabled) {
      onClose();
      return;
    }

    const lastShown = localStorage.getItem(WELCOME_LAST_SHOWN_KEY);
    const today = new Date().toDateString();
    
    if (lastShown !== today && currentDay && currentDay >= 1 && currentDay <= 30) {
      setIsVisible(true);
      localStorage.setItem(WELCOME_LAST_SHOWN_KEY, today);
    } else {
      onClose();
    }
  }, [currentDay, onClose]);

  const handleContinue = () => {
    setIsVisible(false);
    onClose();
    navigate(`/day/${currentDay}`);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    onClose();
  };

  const handleDisableWelcome = () => {
    localStorage.setItem(WELCOME_DISABLED_KEY, 'true');
    setIsVisible(false);
    onClose();
  };

  if (!isVisible || !todayInfo) return null;

  return (
    <div className="fixed inset-0 z-[10001] bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`bg-gradient-to-br from-${pillarColor}-500 to-${pillarColor}-600 p-6 text-white relative`}>
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
            <GreetingIcon className="w-4 h-4" />
            <span>{greeting.text}</span>
          </div>
          
          <h1 className="font-heading text-2xl font-bold mb-1">
            Day {currentDay}
          </h1>
          <div className="flex items-center gap-2">
            <PillarIcon className="w-4 h-4" />
            <span className="text-white/90">{pillar}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Today's Scripture */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-xs text-amber-600 font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Today's Scripture
            </p>
            <p className="font-heading text-[var(--primary)] font-semibold">{todayInfo.scripture}</p>
            <p className="text-sm text-gray-600 mt-2 italic line-clamp-2">
              "{todayInfo.scriptureText?.substring(0, 100)}..."
            </p>
          </div>

          {/* Yesterday's Reflection */}
          {yesterdayNote && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                Yesterday's Reflection
              </p>
              <p className="text-sm text-gray-600 line-clamp-2">{yesterdayNote}</p>
            </div>
          )}

          {/* Daily Challenge */}
          {todayChallenge && (
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200">
              <p className="text-xs text-violet-600 font-bold uppercase tracking-wider mb-2">
                Today's Challenge
              </p>
              <p className="text-sm text-violet-800">{todayChallenge}</p>
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
          >
            Continue Journey
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Disable Option */}
          <button
            onClick={handleDisableWelcome}
            className="w-full text-gray-400 text-xs hover:text-gray-600 transition-colors"
          >
            Don't show this again
          </button>
        </div>
      </div>
    </div>
  );
};

// Export helper to check if welcome should show
export const shouldShowWelcome = (currentDay) => {
  const isDisabled = localStorage.getItem(WELCOME_DISABLED_KEY) === 'true';
  if (isDisabled) return false;
  
  const lastShown = localStorage.getItem(WELCOME_LAST_SHOWN_KEY);
  const today = new Date().toDateString();
  
  return lastShown !== today && currentDay && currentDay >= 1 && currentDay <= 30;
};

// Export helper to re-enable welcome
export const enableWelcome = () => {
  localStorage.removeItem(WELCOME_DISABLED_KEY);
};

export default WelcomeBackModal;
